import { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import roavaWebsiteLogo from "@/assets/bundled/roava-website-logo-transparent.png";
import { cn } from "@/lib/utils";

/**
 * RoavaPage — canonical page wrapper for user-facing screens.
 *
 * Provides:
 *   - warm off-white page background
 *   - 24px horizontal padding (max-w 480 for phone-shaped layouts)
 *   - optional sticky header with logo, title, subtitle, back link, action slot
 *   - bottom safe-area padding via .pb-nav-safe (driven by
 *     --bottom-nav-height published by BottomTabs.tsx)
 *
 * Screen.tsx is intentionally left untouched in Phase 1. Screens migrate
 * onto RoavaPage one at a time to keep visual diffs auditable.
 */
export function RoavaPage({
  title,
  subtitle,
  children,
  action,
  back,
  showLogo = true,
  className,
}: {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  action?: ReactNode;
  back?: { to: string; label?: string };
  showLogo?: boolean;
  className?: string;
}) {
  const hasHeader = !!(title || action || back || showLogo);
  return (
    <div
      className={cn("min-h-screen w-full max-w-[100vw] overflow-x-clip bg-[var(--roava-bg)]", className)}
      style={{
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
      }}
    >
      {/* Opaque shim that covers the iOS status-bar / Dynamic Island region
          so no card can ever visibly scroll under the notch, even during
          rubber-band frames. Sticks above the page header. */}
      <div
        aria-hidden
        className="sticky top-0 z-40 w-full bg-[var(--roava-bg)] pointer-events-none"
        style={{ height: "env(safe-area-inset-top)" }}
      />
      <div
        className="mx-auto min-w-0 w-full max-w-[480px] box-border px-6 pb-nav-safe"
        style={{
          paddingTop: hasHeader ? 0 : "calc(max(env(safe-area-inset-top), 2.75rem) + 0.5rem)",
        }}
      >

        {hasHeader && (
          <header
            className="sticky top-0 z-30 -mx-6 px-6 mb-6 bg-[var(--roava-bg)] pb-3"
            style={{ paddingTop: "calc(max(env(safe-area-inset-top), 2.75rem) + 0.5rem)" }}
          >
            {back && (
              <Link
                to={back.to}
                className="-ml-1 mb-2 inline-flex items-center gap-1 text-sm font-medium text-[var(--roava-text-muted)] hover:text-[var(--roava-text)] transition-colors"
              >
                <ChevronLeft className="size-4" />
                {back.label ?? "Back"}
              </Link>
            )}

            {showLogo && (
              <img
                src={roavaWebsiteLogo}
                alt="Roava by WGP Apps"
                className="h-auto w-full max-w-[170px]"
                style={{ mixBlendMode: "multiply" }}
              />
            )}

            {(title || action) && (
              <div className="mt-3 flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  {title && <h1 className="text-2xl font-bold tracking-tight text-[var(--roava-text)]">{title}</h1>}
                  {subtitle && <p className="mt-1 text-sm text-[var(--roava-text-muted)]">{subtitle}</p>}
                </div>
                {action ? <div className="shrink-0">{action}</div> : null}
              </div>
            )}
          </header>
        )}

        {children}
      </div>
    </div>
  );
}
